import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useUpgradeModal } from '@/context/UpgradeModalContext';
import type { AdminStats, AdminFrequencyStatus, AdminPlanInfo, AdminLicenseInfo, AdminDomainStatus, AdminSocialStatus, AdminUpcomingEvent, AdminStation } from '@/mocks/adminDashboard';
import type { RadioScoreData } from '@/components/feature/RadioScore';
import RadioScore from '@/components/feature/RadioScore';

interface WidgetsGridProps {
  stats: AdminStats | null;
  frequency: AdminFrequencyStatus | null;
  plan: AdminPlanInfo | null;
  license: AdminLicenseInfo | null;
  domain: AdminDomainStatus | null;
  social: AdminSocialStatus | null;
  upcomingEvents: AdminUpcomingEvent[];
  station: AdminStation | null;
  radioScore: RadioScoreData | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

// ── helpers ──
function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

const streamStatusCfg: Record<string, { label: string; dot: string; text: string; icon: string }> = {
  online: { label: 'Online', dot: 'bg-emerald-500', text: 'text-emerald-600', icon: 'ri-checkbox-circle-fill' },
  inestable: { label: 'Inestable', dot: 'bg-amber-500', text: 'text-amber-600', icon: 'ri-error-warning-fill' },
  offline: { label: 'Offline', dot: 'bg-red-500', text: 'text-red-600', icon: 'ri-close-circle-fill' },
  error: { label: 'Error', dot: 'bg-orange-500', text: 'text-orange-600', icon: 'ri-bug-fill' },
  sin_respuesta: { label: 'Sin respuesta', dot: 'bg-gray-400', text: 'text-gray-500', icon: 'ri-question-fill' },
};

const freqStatusCfg: Record<string, { label: string; dot: string; text: string }> = {
  activa: { label: 'Activa', dot: 'bg-emerald-500', text: 'text-emerald-600' },
  reservada: { label: 'Reservada', dot: 'bg-sky-500', text: 'text-sky-600' },
  liberada: { label: 'Liberada', dot: 'bg-red-500', text: 'text-red-600' },
  pendiente: { label: 'Pendiente', dot: 'bg-amber-500', text: 'text-amber-600' },
};

const licenseStatusCfg: Record<string, { label: string; dot: string; text: string }> = {
  activa: { label: 'Activa', dot: 'bg-emerald-500', text: 'text-emerald-600' },
  proxima_vencer: { label: 'Por vencer', dot: 'bg-amber-500', text: 'text-amber-600' },
  vencida: { label: 'Vencida', dot: 'bg-red-500', text: 'text-red-600' },
};

const domainStatusCfg: Record<string, { label: string; dot: string; text: string }> = {
  connected: { label: 'Conectado', dot: 'bg-emerald-500', text: 'text-emerald-600' },
  error: { label: 'Error', dot: 'bg-red-500', text: 'text-red-600' },
  pending: { label: 'Pendiente', dot: 'bg-amber-500', text: 'text-amber-600' },
  not_configured: { label: 'No configurado', dot: 'bg-gray-400', text: 'text-gray-500' },
};

export default function WidgetsGrid({ stats, frequency, plan, license, domain, social, upcomingEvents, station, radioScore, loading, error, onRetry }: WidgetsGridProps) {
  const showDetail = useCallback(async (title: string, html: string) => {
    await Swal.fire({
      title,
      html: `<div style="text-align:left;font-size:13px;line-height:1.6;">${html}</div>`,
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#1f2937',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    });
  }, []);

  const { openUpgradeModal } = useUpgradeModal();

  // ── Loading ──
  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="w-36 h-5 bg-background-200 rounded-lg animate-pulse"></div>
          <div className="w-28 h-5 bg-background-200 rounded-full animate-pulse"></div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-4 min-h-[110px]">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-lg bg-background-200 animate-pulse"></div>
                <div className="w-20 h-3 bg-background-200 rounded animate-pulse"></div>
              </div>
              <div className="w-16 h-7 bg-background-200 rounded-lg animate-pulse mb-2"></div>
              <div className="w-24 h-3 bg-background-200 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div>
        <h3 className="text-foreground-950 font-semibold text-sm mb-4">Panel de Widgets</h3>
        <div className="bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
          <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-3">
            <i className="ri-error-warning-line text-red-500 text-xl"></i>
          </div>
          <h4 className="text-foreground-800 text-sm font-semibold mb-1">Error cargando widgets</h4>
          <p className="text-foreground-400 text-xs mb-4">No se pudieron cargar los datos del panel.</p>
          <button onClick={onRetry} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap">
            <i className="ri-refresh-line text-xs"></i>Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (!stats || !station || !frequency || !plan || !license || !domain || !social || !radioScore) return null;

  const streamCfg = streamStatusCfg[station.isLive ? 'online' : 'offline'];
  const freqCfg = freqStatusCfg[frequency.status];
  const licCfg = licenseStatusCfg[license.status];
  const domCfg = domainStatusCfg[domain.status];

  const socialConnected = [social.facebook, social.youtube, social.instagram, social.twitter].filter(s => s.connected).length;
  const socialTotal = 4;

  const eventsNextDate = upcomingEvents.length > 0 ? upcomingEvents[0].date : '—';

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-foreground-950 font-semibold text-sm">Panel de Widgets</h3>
        <span className="text-foreground-400 text-[10px] bg-background-100 px-2 py-0.5 rounded-full">
          Actualizado ahora
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {/* 1 ─ Stream Status */}
        <WidgetCard
          icon="ri-signal-wifi-line"
          iconBg="bg-emerald-100"
          iconColor="text-emerald-600"
          label="Estado Stream"
          idx={0}
          onClick={() => showDetail('Estado del Stream',
            `<p style="margin-bottom:10px;">Estado actual de tu transmisión. Si está caída, los oyentes no pueden escucharte.</p>
            <div style="background:#f0fdf4;border-radius:8px;padding:8px 12px;margin-bottom:8px;">
              <strong style="color:#059669;">Online · ${station.listeners.toLocaleString()} oyentes</strong>
            </div>
            <p style="color:#6b7280;font-size:11px;">Stream URL: ${station.streamUrl}</p>`
          )}
        >
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${streamCfg.dot} ${station.isLive ? 'animate-pulse' : ''}`}></span>
            <span className={`text-lg font-bold ${streamCfg.text}`}>{streamCfg.label}</span>
          </div>
          <span className="text-foreground-400 text-[10px] mt-1 block">{station.listeners.toLocaleString()} oyentes</span>
        </WidgetCard>

        {/* 2 ─ Frequency Status */}
        <WidgetCard
          icon="ri-radar-line"
          iconBg="bg-sky-100"
          iconColor="text-sky-600"
          label="Estado Frecuencia"
          idx={1}
          onClick={() => showDetail('Estado de Frecuencia',
            `<p style="margin-bottom:10px;">Tu frecuencia ${frequency.frequency} está <strong style="color:${freqCfg.text === 'text-emerald-600' ? '#059669' : freqCfg.text === 'text-red-600' ? '#dc2626' : '#d97706'};">${freqCfg.label}</strong>.</p>
            <div style="background:#f3f4f6;border-radius:8px;padding:8px 12px;margin-bottom:8px;">
              <p style="font-size:12px;">Asignada: ${frequency.assignedDate}</p>
              <p style="font-size:12px;">Vence: ${frequency.expiresDate}</p>
            </div>`
          )}
        >
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${freqCfg.dot}`}></span>
            <span className={`text-lg font-bold ${freqCfg.text}`}>{freqCfg.label}</span>
          </div>
          <span className="text-foreground-400 text-[10px] mt-1 block">{frequency.frequency}</span>
        </WidgetCard>

        {/* 3 ─ Oyentes Hoy */}
        <WidgetCard
          icon="ri-headphone-line"
          iconBg="bg-accent-100"
          iconColor="text-accent-600"
          label="Oyentes Hoy"
          idx={2}
          onClick={() => showDetail('Oyentes Hoy',
            `<p style="margin-bottom:10px;">Total de reproducciones acumuladas desde las 00:00 UTC.</p>
            <div style="background:#f3f4f6;border-radius:8px;padding:8px 12px;margin-bottom:8px;">
              <strong style="font-size:18px;">${fmt(stats.reproduccionesHoy)}</strong>
              <p style="font-size:11px;color:#6b7280;">Pico hoy: ${fmt(Math.round(stats.reproduccionesHoy * 0.04))} · Promedio: ${fmt(Math.round(stats.reproduccionesHoy / 24))}/h</p>
            </div>`
          )}
        >
          <p className="text-foreground-950 text-2xl font-bold">{fmt(stats.reproduccionesHoy)}</p>
          <span className="text-emerald-600 text-[10px] font-medium mt-1 block">+7.9% vs ayer</span>
        </WidgetCard>

        {/* 4 ─ Oyentes Mes */}
        <WidgetCard
          icon="ri-bar-chart-2-line"
          iconBg="bg-violet-100"
          iconColor="text-violet-600"
          label="Oyentes Mes"
          idx={3}
          onClick={() => showDetail('Oyentes del Mes',
            `<p style="margin-bottom:10px;">Total de reproducciones acumuladas este mes.</p>
            <div style="background:#f3f4f6;border-radius:8px;padding:8px 12px;margin-bottom:8px;">
              <strong style="font-size:18px;">${fmt(stats.oyentesMes)}</strong>
              <p style="font-size:11px;color:#6b7280;">Mes pasado: ${fmt(Math.round(stats.oyentesMes * 0.88))} · Proyección: ${fmt(Math.round(stats.oyentesMes * 1.12))}</p>
            </div>`
          )}
        >
          <p className="text-foreground-950 text-2xl font-bold">{fmt(stats.oyentesMes)}</p>
          <span className="text-emerald-600 text-[10px] font-medium mt-1 block">+12.3% vs mes pasado</span>
        </WidgetCard>

        {/* 5 ─ Mensajes Cabina */}
        <WidgetCard
          icon="ri-chat-3-line"
          iconBg="bg-rose-100"
          iconColor="text-rose-600"
          label="Mensajes Cabina"
          idx={4}
          linkTo="/admin/cabina"
          onClick={() => showDetail('Mensajes en Cabina',
            `<p style="margin-bottom:10px;">Total de mensajes recibidos hoy. Incluye saludos, pedidos de canciones, concursos y mensajes de voz.</p>
            <div style="background:#f3f4f6;border-radius:8px;padding:8px 12px;margin-bottom:8px;">
              <strong style="font-size:18px;">${fmt(stats.mensajesCabina)}</strong>
              <p style="font-size:11px;color:#6b7280;">12 pendientes · 89% respondidos</p>
            </div>
            <a href="/admin/cabina" style="font-size:11px;font-weight:600;color:#f59e0b;text-decoration:none;">Ir a Cabina →</a>`
          )}
        >
          <p className="text-foreground-950 text-2xl font-bold">{fmt(stats.mensajesCabina)}</p>
          <span className="text-amber-600 text-[10px] font-medium mt-1 block">12 pendientes</span>
        </WidgetCard>

        {/* 6 ─ Lives Activos */}
        <WidgetCard
          icon="ri-live-line"
          iconBg="bg-red-100"
          iconColor="text-red-600"
          label="Lives Activos"
          idx={5}
          linkTo="/admin/live"
          onClick={() => showDetail('Lives Activos',
            `<p style="margin-bottom:10px;">Transmisiones en vivo actualmente al aire.</p>
            <div style="background:#f3f4f6;border-radius:8px;padding:8px 12px;margin-bottom:8px;">
              <strong style="font-size:18px;">${stats.livesActivos} en vivo</strong>
              <p style="font-size:11px;color:#6b7280;">Total histórico: ${stats.lives} lives · Último: Tardes de Pop</p>
            </div>
            <a href="/admin/live" style="font-size:11px;font-weight:600;color:#f59e0b;text-decoration:none;">Gestionar Lives →</a>`
          )}
        >
          <div className="flex items-center gap-2 mb-1">
            <p className="text-foreground-950 text-2xl font-bold">{stats.livesActivos}</p>
            {stats.livesActivos > 0 && (
              <span className="flex items-center gap-1 bg-red-50 px-1.5 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                <span className="text-red-600 text-[9px] font-bold">EN VIVO</span>
              </span>
            )}
          </div>
          <span className="text-foreground-400 text-[10px] mt-1 block">{stats.lives} total histórico</span>
        </WidgetCard>

        {/* 7 ─ Eventos Próximos */}
        <WidgetCard
          icon="ri-calendar-event-line"
          iconBg="bg-indigo-100"
          iconColor="text-indigo-600"
          label="Eventos Próximos"
          idx={6}
          onClick={() => showDetail('Eventos Próximos',
            `<p style="margin-bottom:10px;">Próximos eventos programados en tu radio.</p>
            <div style="background:#f3f4f6;border-radius:8px;padding:8px 12px;margin-bottom:8px;">
              ${upcomingEvents.map((e, i) => `<p style="font-size:12px;margin-bottom:${i < upcomingEvents.length - 1 ? '6px' : '0'};">${i + 1}. <strong>${e.title}</strong> — ${new Date(e.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} ${e.time}</p>`).join('')}
            </div>`
          )}
        >
          <p className="text-foreground-950 text-2xl font-bold">{stats.eventosProximos}</p>
          <span className="text-foreground-400 text-[10px] mt-1 block">Próximo: {new Date(eventsNextDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}</span>
        </WidgetCard>

        {/* 8 ─ Storage Utilizado */}
        <WidgetCard
          icon="ri-hard-drive-2-line"
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
          label="Storage Utilizado"
          idx={7}
          linkTo="/admin/photos"
          onClick={() => showDetail('Almacenamiento Utilizado',
            `<p style="margin-bottom:10px;">Espacio ocupado de tu plan actual. Fotos, videos y archivos de audio cuentan para este límite.</p>
            <div style="background:#f3f4f6;border-radius:8px;padding:8px 12px;margin-bottom:8px;">
              <strong style="font-size:18px;">${stats.storageUsadoGB} / ${stats.storageTotalGB} GB</strong>
              <p style="font-size:11px;color:#6b7280;">${Math.round((stats.storageUsadoGB / stats.storageTotalGB) * 100)}% usado · ${(stats.storageTotalGB - stats.storageUsadoGB).toFixed(1)} GB libres</p>
            </div>`
          )}
        >
          <p className="text-foreground-950 text-2xl font-bold">{stats.storageUsadoGB} <span className="text-foreground-400 text-sm font-medium">/ {stats.storageTotalGB} GB</span></p>
          <div className="mt-2 h-1.5 bg-background-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${
                (stats.storageUsadoGB / stats.storageTotalGB) * 100 > 80
                  ? 'bg-red-500'
                  : (stats.storageUsadoGB / stats.storageTotalGB) * 100 > 50
                    ? 'bg-amber-400'
                    : 'bg-primary-500'
              }`}
              style={{ width: `${Math.min((stats.storageUsadoGB / stats.storageTotalGB) * 100, 100)}%` }}
            ></div>
          </div>
        </WidgetCard>

        {/* 9 ─ Plan Actual */}
        <WidgetCard
          icon="ri-vip-diamond-line"
          iconBg={plan.type === 'free' ? 'bg-secondary-100' : plan.type === 'pro' ? 'bg-primary-100' : 'bg-accent-100'}
          iconColor={plan.type === 'free' ? 'text-secondary-600' : plan.type === 'pro' ? 'text-primary-600' : 'text-accent-600'}
          label="Plan Actual"
          idx={8}
          onClick={openUpgradeModal}
        >
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
              plan.type === 'free' ? 'bg-secondary-100 text-secondary-600' : plan.type === 'pro' ? 'bg-primary-100 text-primary-600' : 'bg-accent-100 text-accent-600'
            }`}>
              {plan.name}
            </span>
          </div>
          <span className="text-foreground-400 text-[10px] mt-1.5 block">{plan.daysRemaining} días restantes</span>
        </WidgetCard>

        {/* 10 ─ Licencia Anual */}
        <WidgetCard
          icon="ri-shield-check-line"
          iconBg={license.status === 'activa' ? 'bg-emerald-100' : license.status === 'proxima_vencer' ? 'bg-amber-100' : 'bg-red-100'}
          iconColor={license.status === 'activa' ? 'text-emerald-600' : license.status === 'proxima_vencer' ? 'text-amber-600' : 'text-red-600'}
          label="Licencia Anual"
          idx={9}
          onClick={() => showDetail('Licencia Anual',
            `<p style="margin-bottom:10px;">Estado de tu licencia anual de operación.</p>
            <div style="background:#f3f4f6;border-radius:8px;padding:8px 12px;margin-bottom:8px;">
              <p style="font-size:12px;">Estado: <strong style="color:${licCfg.dot === 'bg-emerald-500' ? '#059669' : licCfg.dot === 'bg-amber-500' ? '#d97706' : '#dc2626'};">${licCfg.label}</strong></p>
              <p style="font-size:12px;">Emitida: ${license.issuedDate}</p>
              <p style="font-size:12px;">Vence: ${license.expiryDate}</p>
              <p style="font-size:12px;">Días restantes: <strong>${license.daysRemaining}</strong></p>
              <p style="font-size:12px;">Precio: ${license.price}</p>
            </div>`
          )}
        >
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${licCfg.dot}`}></span>
            <span className={`text-lg font-bold ${licCfg.text}`}>{licCfg.label}</span>
          </div>
          <span className="text-foreground-400 text-[10px] mt-1 block">{license.daysRemaining} días · {license.price}</span>
        </WidgetCard>

        {/* 11 ─ Estado Dominio */}
        <WidgetCard
          icon="ri-global-line"
          iconBg="bg-cyan-100"
          iconColor="text-cyan-600"
          label="Estado Dominio"
          idx={10}
          linkTo="/admin/settings/domain"
          onClick={() => showDetail('Estado del Dominio',
            `<p style="margin-bottom:10px;">Configuración de tu dominio personalizado.</p>
            <div style="background:#f3f4f6;border-radius:8px;padding:8px 12px;margin-bottom:8px;">
              <p style="font-size:12px;">Dominio: <strong>${domain.domain}</strong></p>
              <p style="font-size:12px;">Estado: <strong style="color:${domCfg.dot === 'bg-emerald-500' ? '#059669' : '#dc2626'};">${domCfg.label}</strong></p>
              <p style="font-size:12px;">SSL: ${domain.sslActive ? '<span style="color:#059669;">Activo</span>' : '<span style="color:#dc2626;">Inactivo</span>'}</p>
              <p style="font-size:11px;color:#6b7280;">Última verificación: ${new Date(domain.lastChecked).toLocaleString('es-ES')}</p>
            </div>`
          )}
        >
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${domCfg.dot}`}></span>
            <span className={`text-lg font-bold ${domCfg.text}`}>{domCfg.label}</span>
          </div>
          <span className="text-foreground-400 text-[10px] mt-1 block truncate max-w-[140px]">{domain.domain}</span>
        </WidgetCard>

        {/* 12 ─ Estado Redes Sociales */}
        <WidgetCard
          icon="ri-share-line"
          iconBg="bg-pink-100"
          iconColor="text-pink-600"
          label="Redes Sociales"
          idx={11}
          linkTo="/admin/social-connect"
          onClick={() => showDetail('Estado de Redes Sociales',
            `<p style="margin-bottom:10px;">Conexiones con tus plataformas sociales.</p>
            <div style="background:#f3f4f6;border-radius:8px;padding:8px 12px;margin-bottom:8px;">
              <p style="font-size:12px;margin-bottom:4px;">Facebook: ${social.facebook.connected ? '<span style="color:#059669;">✓ Conectado</span>' : '<span style="color:#dc2626;">✗ Desconectado</span>'} — ${social.facebook.pageName}</p>
              <p style="font-size:12px;margin-bottom:4px;">YouTube: ${social.youtube.connected ? '<span style="color:#059669;">✓ Conectado</span>' : '<span style="color:#dc2626;">✗ Desconectado</span>'} — ${social.youtube.channelName}</p>
              <p style="font-size:12px;margin-bottom:4px;">Instagram: ${social.instagram.connected ? '<span style="color:#059669;">✓ Conectado</span>' : '<span style="color:#dc2626;">✗ Desconectado</span>'} — ${social.instagram.username}</p>
              <p style="font-size:12px;">Twitter/X: ${social.twitter.connected ? '<span style="color:#059669;">✓ Conectado</span>' : '<span style="color:#dc2626;">✗ Desconectado</span>'} — ${social.twitter.username}</p>
            </div>`
          )}
        >
          <p className="text-foreground-950 text-2xl font-bold">{socialConnected}/{socialTotal}</p>
          <span className="text-foreground-400 text-[10px] mt-1 block">
            {socialConnected === socialTotal ? 'Todas conectadas' : `${socialTotal - socialConnected} desconectadas`}
          </span>
        </WidgetCard>

        {/* 13 ─ Radio Score */}
        <div
          className="bg-background-50 border border-background-200 rounded-2xl p-4 hover:border-background-300 transition-colors cursor-pointer group min-h-[115px] flex flex-col"
          style={{
            animation: `fadeInUp 0.4s ease-out forwards`,
            animationDelay: `${12 * 40}ms`,
            opacity: 0,
          }}
        >
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <i className="ri-speed-up-line text-amber-600 text-sm"></i>
            </div>
            <span className="text-foreground-400 text-[10px] font-medium leading-tight">Radio Score</span>
            <span className="ml-auto text-foreground-300 text-[9px] font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 whitespace-nowrap">
              Detalle <i className="ri-arrow-right-s-line text-[9px]"></i>
            </span>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <RadioScore data={radioScore} size="sm" showLabel={true} radioName={station.name} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Widget Card wrapper ──
function WidgetCard({ icon, iconBg, iconColor, label, idx, linkTo, onClick, children }: {
  icon: string;
  iconBg: string;
  iconColor: string;
  label: string;
  idx: number;
  linkTo?: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  const content = (
    <div
      className="bg-background-50 border border-background-200 rounded-2xl p-4 hover:border-background-300 transition-colors cursor-pointer group min-h-[115px] flex flex-col"
      style={{
        animation: `fadeInUp 0.4s ease-out forwards`,
        animationDelay: `${idx * 40}ms`,
        opacity: 0,
      }}
      onClick={onClick}
    >
      <div className="flex items-center gap-2.5 mb-2">
        <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
          <i className={`${icon} ${iconColor} text-sm`}></i>
        </div>
        <span className="text-foreground-400 text-[10px] font-medium leading-tight">{label}</span>
        <span className="ml-auto text-foreground-300 text-[9px] font-medium opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 whitespace-nowrap">
          Detalle <i className="ri-arrow-right-s-line text-[9px]"></i>
        </span>
      </div>
      <div className="flex-1 flex flex-col justify-center">
        {children}
      </div>
    </div>
  );

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>;
  }
  return content;
}